import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, authService, adminService } from '../api/apiClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(!!api.getAdminBackupToken());

  const fetchUser = useCallback(async () => {
    const token = api.getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await authService.getUser();
      if (response && (response.user || response.data)) {
        setUser(response.user || response.data);
      } else {
        setUser(response);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      api.setToken(null);
      api.setAdminBackupToken(null);
      setUser(null);
      setIsImpersonating(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (e) {
      // Ignore network errors during logout
    } finally {
      api.setToken(null);
      api.setAdminBackupToken(null);
      setUser(null);
      setIsImpersonating(false);
    }
  }, []);

  const stopImpersonation = useCallback(async () => {
    const adminToken = api.getAdminBackupToken();
    if (adminToken) {
      api.setToken(adminToken);
      api.setAdminBackupToken(null);
      setIsImpersonating(false);
      await fetchUser();
    } else {
      await logout();
    }
  }, [fetchUser, logout]);

  useEffect(() => {
    fetchUser();

    const handleUnauthorized = () => {
      if (api.getAdminBackupToken()) {
        stopImpersonation();
      } else {
        api.setToken(null);
        setUser(null);
      }
    };

    window.addEventListener('quinvoice:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('quinvoice:unauthorized', handleUnauthorized);
  }, [fetchUser, stopImpersonation]);

  const login = async (email, password) => {
    const res = await authService.login({ email, password });
    if (res && res.token) {
      api.setToken(res.token);
      await fetchUser();
      return res;
    }
    throw new Error(res.message || 'No se pudo iniciar sesión.');
  };

  const register = async (data) => {
    const res = await authService.register(data);
    if (res && res.token) {
      api.setToken(res.token);
      await fetchUser();
      return res;
    }
    throw new Error(res.message || 'No se pudo completar el registro.');
  };

  const impersonate = async (userId) => {
    const currentAdminToken = api.getToken();
    const res = await adminService.impersonate(userId);
    if (res && res.token) {
      api.setAdminBackupToken(currentAdminToken);
      api.setToken(res.token);
      setIsImpersonating(true);
      await fetchUser();
      return res;
    }
    throw new Error(res.message || 'No se pudo iniciar impersonación.');
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: !!(user && (user.is_admin || user.account_type === 1)),
    isSubscribed: !!(user && (user.is_subscribed || user.account_type === 1 || user.is_admin)),
    isImpersonating,
    login,
    register,
    logout,
    impersonate,
    stopImpersonation,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
